import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Globe, 
  Phone, 
  Mail, 
  Building,
  Image as ImageIcon,
  ArrowLeft,
  Plus
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorGalleryDetail.css';

const CuratorGalleryDetail = () => {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  
  const [gallery, setGallery] = useState(null);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('newest'); // newest | oldest | a_to_z | z_to_a | featured

  useEffect(() => {
    if (galleryId) {
      loadGalleryDetails();
    }
  }, [galleryId]);

  const loadGalleryDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user to verify access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Verify curator has access to this gallery
      const { data: accessCheck } = await supabase
        .from('gallery_access')
        .select('access_level')
        .eq('user_uuid', user.id)
        .eq('gallery_id', galleryId)
        .single();

      if (!accessCheck) {
        setError('You do not have access to this gallery');
        return;
      }

      // Load gallery details
      const { data: galleryData, error: galleryError } = await supabase
        .from('galleries')
        .select('*')
        .eq('gallery_id', galleryId)
        .single();

      if (galleryError) throw galleryError;
      setGallery(galleryData);

      // Load collections for this gallery
      const { data: collectionsData, error: collectionsError } = await supabase
        .from('gallery_collection_mapping')
        .select(`
          collection_id,
          collections (
            collection_id,
            title,
            description,
            cover_image,
            start_date,
            end_date,
            created_at,
            access_control,
            featured,
            type
          )
        `)
        .eq('gallery_id', galleryId)
        .order('collections(created_at)', { ascending: false });

      if (collectionsError) throw collectionsError;

      // Flatten to a single list and sort by created_at (newest first)
      const flatCollections = (collectionsData || [])
        .map((item) => item.collections)
        .filter(Boolean)
        .sort((a, b) => new Date(b.created_at || b.start_date || 0) - new Date(a.created_at || a.start_date || 0));

  setCollections(flatCollections);

    } catch (err) {
      console.error('Error loading gallery details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionClick = (collectionId) => {
    navigate(`/curator/collections/${collectionId}`);
  };

  const CollectionCard = ({ collection }) => (
    <div 
      className="collection-item"
      onClick={() => handleCollectionClick(collection.collection_id)}
    >
      <div className="collection-thumbnail">
        {collection.cover_image ? (
          <img src={collection.cover_image} alt={collection.title} />
        ) : (
          <div className="collection-placeholder">
            <ImageIcon size={32} />
          </div>
        )}
        {collection.featured && <div className="featured-badge">Featured</div>}
      </div>
      
      <div className="collection-info">
        <h4 className="collection-title">{collection.title}</h4>
        {collection.description && (
          <p className="collection-description">{collection.description}</p>
        )}
        <div className="collection-meta">
          <span className="collection-type">{collection.type || 'Collection'}</span>
          <span className="collection-access">{collection.access_control}</span>
        </div>
      </div>
    </div>
  );

  // Memoized sorted list based on selected sort option
  const sortedCollections = useMemo(() => {
    const arr = [...(collections || [])];
    const safeDate = (d) => (d ? new Date(d).getTime() : 0);
    switch (sortOption) {
      case 'oldest':
        arr.sort((a, b) => safeDate(a.created_at || a.start_date) - safeDate(b.created_at || b.start_date));
        break;
      case 'a_to_z':
        arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'z_to_a':
        arr.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
        break;
      case 'featured':
        arr.sort((a, b) => {
          if (a.featured === b.featured) {
            return safeDate(b.created_at || b.start_date) - safeDate(a.created_at || a.start_date);
          }
          // Put true before false
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        });
        break;
      case 'newest':
      default:
        arr.sort((a, b) => safeDate(b.created_at || b.start_date) - safeDate(a.created_at || a.start_date));
    }
    return arr;
  }, [collections, sortOption]);

  if (loading) {
    return (
      <div className="curator-gallery-detail">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading gallery details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="curator-gallery-detail">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/curator')} className="btn-primary">
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="curator-gallery-detail">
        <div className="error-state">
          <h3>Gallery Not Found</h3>
          <p>The gallery you're looking for doesn't exist or you don't have access to it.</p>
          <button onClick={() => navigate('/curator')} className="btn-primary">
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-gallery-detail">
      {/* Back Navigation */}
      <button 
        className="back-button"
        onClick={() => navigate('/curator')}
      >
        <ArrowLeft size={20} />
        Back to Galleries
      </button>

      {/* Cover Image Section - Instagram-style */}
      <div className="cover-image-section">
        {gallery.cover_image ? (
          <img 
            src={gallery.cover_image} 
            alt={gallery.name}
            className="cover-image"
          />
        ) : (
          <div className="cover-placeholder">
            <Building size={64} />
            <p>No cover image</p>
          </div>
        )}
      </div>

      {/* Gallery Basic Info - Below cover image */}
      <div className="gallery-basic-info">
        <div className="gallery-header">
          <h1 className="gallery-name">{gallery.name}</h1>
        </div>
        
        {gallery.description && (
          <p className="gallery-description">{gallery.description}</p>
        )}
        
        <div className="gallery-details">
          {gallery.address && (
            <div className="detail-item">
              <MapPin size={16} />
              <span>{gallery.address}</span>
            </div>
          )}
          {gallery.phone && (
            <div className="detail-item">
              <Phone size={16} />
              <span>{gallery.phone}</span>
            </div>
          )}
          {gallery.email && (
            <div className="detail-item">
              <Mail size={16} />
              <span>{gallery.email}</span>
            </div>
          )}
          {gallery.website && (
            <div className="detail-item">
              <Globe size={16} />
              <a href={gallery.website} target="_blank" rel="noopener noreferrer">
                {gallery.website}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Collections List */}
      <div className="collections-container">
        <div className="collections-header">
          <h2>Gallery Collections</h2>
          <div className="collections-controls">
            <label htmlFor="sort-select" className="visually-hidden">Sort by</label>
            <select
              id="sort-select"
              className="sort-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="a_to_z">A–Z</option>
              <option value="z_to_a">Z–A</option>
              <option value="featured">Featured first</option>
            </select>
          </div>
          <button 
            className="create-collection-btn"
            onClick={() => navigate(`/curator/galleries/${galleryId}/create-collection`)}
          >
            <Plus size={16} />
            Create Collection
          </button>
        </div>

        {sortedCollections.length === 0 ? (
          <div className="empty-section">
            <p>No collections yet</p>
          </div>
        ) : (
          <div className="collections-grid">
            {sortedCollections.map((collection) => (
              <CollectionCard
                key={collection.collection_id}
                collection={collection}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CuratorGalleryDetail;